import { create } from 'zustand'
import restaurant from '@/assets/biz-restaurant.jpg'
import retail from '@/assets/biz-retail.jpg'
import barber from '@/assets/biz-barber.jpg'
import hardware from '@/assets/biz-hardware.jpg'
import { fetchLandingContent, saveLandingSection } from './landingContentApi'
// Las dos páginas del conector de IA (MCP) llevan su semilla en un `.md`: el
// texto trae comandos y nombres entre comillas invertidas que dentro de una
// plantilla de JS habría que escapar uno a uno.
import aiConnectorSeed from './semillas/conector-ia.md?raw'
import aiConnectorPrivacySeed from './semillas/conector-ia-privacidad.md?raw'

/**
 * CONTENIDO EDITABLE DEL LANDING — fuente de verdad: la BD del backend.
 *
 * Al iniciar la app se carga del backend (`load`) y el panel admin guarda en el
 * backend (`save`). Los `DEFAULT_*` son solo la semilla del CMS (lo que ve el
 * editor con la BD vacía): las vistas públicas NO los renderizan mientras se
 * carga — muestran esqueletos — ni cuando la carga falla — muestran un error.
 * Única excepción: las páginas legales (`LegalPage`), que si la carga falla
 * enseñan su semilla, porque una política ilegible es motivo de rechazo en
 * Google Play (Adrian, 2026-09-28).
 * Existe además una caché en localStorage del último contenido REAL recibido
 * (stale-while-revalidate) para que, con mala conexión, las visitas repetidas
 * vean contenido de verdad al instante. Las imágenes subidas se guardan como
 * data URL dentro del documento (prototipo; futuro: almacenamiento con URL).
 */

export interface GrowingBusiness {
  id: string
  name: string
  logo: string
}

/** Pregunta/respuesta desplegable de una categoría. */
export interface BizFaq {
  q: string
  a: string
}

/** Testimonio asociado a un tipo de negocio (va debajo de su imagen). */
export interface BizTestimonial {
  quote: string
  name: string
  role: string
}

export interface BusinessTypeItem {
  id: string
  /** Ícono por defecto (lucide) — respaldo si no se sube un SVG. */
  iconKey: 'coffee' | 'shopping' | 'sparkles' | 'wrench' | 'building'
  /** SVG subido (URL). Si está presente, tiene prioridad sobre `iconKey`. */
  iconSvg?: string
  label: string
  tagline: string
  description: string
  children: string[]
  banner: string
  link: string
  faqs: BizFaq[]
  testimonial: BizTestimonial
}

/** Módulo del ecosistema TecoPOS. Al pasar el cursor se abre un modal con la
 *  descripción y, opcionalmente, un botón con enlace. */
export interface EcosystemItem {
  id: string
  /** Ícono por defecto (lucide) — respaldo si no se sube un SVG. */
  iconKey:
    | 'cart'
    | 'package'
    | 'globe'
    | 'card'
    | 'wallet'
    | 'chart'
    | 'clock'
    | 'users'
    | 'receipt'
    | 'building'
    | 'sparkles'
  /** SVG subido (URL). Si está presente, tiene prioridad sobre `iconKey`. */
  iconSvg?: string
  label: string
  description: string
  buttonLabel: string
  buttonHref: string
}

/** Diapositiva del carrusel a todo lo ancho (imagen + enlace). */
export interface CarouselSlide {
  id: string
  image: string
  link: string
}

/** Plan de precios editable. */
export interface PricingPlan {
  id: string
  name: string
  tag: string
  /** Precio "libre" de respaldo (ej. "Cotización personalizada") si no hay mensual/anual. */
  price: string
  /** Precio en modo mensual (ej. "$25"). Vacío → usa `price`. */
  priceMonthly: string
  /** Precio en modo anual, por mes (ej. "$20"). Vacío → usa `price`. */
  priceAnnual: string
  /** Etiqueta de ahorro que aparece en modo anual (ej. "Ahorra 20%"). */
  annualBadge: string
  /**
   * Plan que solo se contrata anualmente: la card muestra siempre su precio anual
   * (ignora el switch Mensual) con la etiqueta "Solo plan anual".
   * Puede faltar en documentos guardados antes de este campo → tratar como false.
   */
  annualOnly?: boolean
  features: string[]
  popular: boolean
  ctaLabel: string
  ctaHref: string
}

/**
 * Plataforma de una aplicación descargable. Es una lista propia y NO se deduce
 * de los enlaces: «Android TV» se descarga del mismo Google Play que Android, y
 * una app puede publicarse en una tienda antes de tener instalador directo.
 */
export type DownloadPlatform = 'android' | 'ios' | 'windows' | 'tv'

/** Etiqueta visible de cada plataforma (las chips y las píldoras de la tarjeta). */
export const DOWNLOAD_PLATFORM_LABELS: Record<DownloadPlatform, string> = {
  android: 'Android',
  ios: 'iPhone y iPad',
  windows: 'Windows',
  tv: 'Android TV',
}

/**
 * Aplicación de la página `/descargas`. Campos planos, como `EcosystemItem`:
 * el editor del CMS los pinta uno a uno y el portal NO aloja los binarios —
 * `apkUrl` y `windowsUrl` son URL que se pegan a mano (hoy, `repo.tecopos.com`).
 *
 * Cada URL es entrada de usuario: la página la pasa por `classifyHref` antes de
 * pintarla, así que un `javascript:` guardado en el CMS nunca llega al DOM.
 */
export interface AppDownload {
  id: string
  name: string
  /** Frase corta bajo el nombre. */
  tagline: string
  description: string
  /** Ícono subido. Vacío → la tarjeta pinta un monograma con las iniciales. */
  icon: string
  platforms: DownloadPlatform[]
  playStoreUrl: string
  appStoreUrl: string
  /** Descarga directa del `.apk` y su versión (ambas a mano). */
  apkUrl: string
  apkVersion: string
  /** Instalador de Windows y su versión (ambas a mano). */
  windowsUrl: string
  windowsVersion: string
  /** `false` → la app no se pinta en la página (se queda guardada en el CMS). */
  visible: boolean
}

/** Las páginas legales en Markdown: cada una tiene su ruta pública y su pestaña en la administración. */
export type LegalDocKey = 'terms' | 'privacy' | 'accountDeletion' | 'aiConnector' | 'aiConnectorPrivacy'

export interface LandingContent {
  announcement: {
    enabled: boolean
    text: string
    ctaLabel: string
    ctaHref: string
  }
  hero: {
    badge: string
    badgeHref: string
    demoVideoUrl: string
    titleLead: string
    titleHighlight: string
    subtitle: string
    primaryCtaLabel: string
    secondaryCtaLabel: string
    socialProofText: string
  }
  growingBusinesses: GrowingBusiness[]
  businessTypes: BusinessTypeItem[]
  ecosystem: EcosystemItem[]
  carousel: CarouselSlide[]
  pricing: PricingPlan[]
  /** Página `/descargas`: aplicaciones instalables del ecosistema. */
  downloads: {
    title: string
    intro: string
    /**
     * Carpeta del canal de actualizaciones de TECOPOS escritorio (la de su
     * `latest.yml`). La lee `tecopos-portal-server`, no esta web; vacía o no
     * `https`, el servidor usa su semilla `https://repo.tecopos.com/PV_Desktop/`.
     * Opcional porque los documentos guardados antes del 2026-10-28 no la traen.
     */
    pvDesktopChannelUrl?: string
    apps: AppDownload[]
  }
  /** Contacto institucional para el botón de WhatsApp del Asistente. */
  contact: {
    whatsapp: string
    whatsappMessage: string
    /** Enlace para "Preguntar a Tecobot" (botón flotante). */
    tecobotUrl: string
  }
  /** Redes sociales institucionales (se muestran en el pie del sitio). */
  social: {
    whatsappChannel: string
    youtube: string
    facebook: string
    instagram: string
  }
  /** Páginas centrales en Markdown (editables desde la administración). */
  terms: string
  privacy: string
  /**
   * Cómo pedir que se borre una cuenta (`/eliminar-cuenta`). Es la URL que
   * Google Play exige declarar para las apps que permiten registrarse.
   */
  accountDeletion: string
  /**
   * Página pública del conector de IA (`/conector-ia`) y su política de
   * privacidad (`/conector-ia/privacidad`). Son las URL que se declaran al
   * enviar el conector al registro MCP y a los directorios de Claude y de
   * ChatGPT (goal mcp-tecopos 23b).
   */
  aiConnector: string
  aiConnectorPrivacy: string
  /** Visibilidad por sección: si `hidden[key]` es true, la sección no se muestra en el sitio. */
  hidden: Record<string, boolean>
}

export const DEFAULT_LANDING_CONTENT: LandingContent = {
  announcement: {
    enabled: false,
    text: '🎉 Nueva versión 4.0 de TecoPOS — modo offline mejorado y nuevos reportes.',
    ctaLabel: 'Ver novedades',
    ctaHref: '/registro',
  },
  hero: {
    badge: 'Nuevo · TecoPOS Card v2.0',
    badgeHref: '/registro',
    demoVideoUrl: 'https://www.youtube.com/embed/aqz-KE-bpKQ',
    titleLead: 'Todo tu negocio.',
    titleHighlight: 'En una sola plataforma.',
    subtitle:
      'Ventas, inventario, tienda online, fidelización y control total desde un mismo lugar.',
    primaryCtaLabel: 'Comenzar ahora',
    secondaryCtaLabel: 'Ver demo',
    socialProofText: '+2,500 negocios activos hoy',
  },
  growingBusinesses: [
    { id: 'gb1', name: 'Meraki Café', logo: '' },
    { id: 'gb2', name: 'Farmacia Plus', logo: '' },
    { id: 'gb3', name: 'Barber Co.', logo: '' },
    { id: 'gb4', name: 'Urban Market', logo: '' },
    { id: 'gb5', name: 'Velvet Store', logo: '' },
    { id: 'gb6', name: 'Prisma Retail', logo: '' },
    { id: 'gb7', name: 'Forza Gym', logo: '' },
    { id: 'gb8', name: 'Lumina Salon', logo: '' },
  ],
  businessTypes: [
    {
      id: 'fnb',
      iconKey: 'coffee',
      label: 'Alimentos y bebidas',
      tagline: 'Para cafeterías, restaurantes y bares',
      description:
        'Optimiza el servicio, gestiona pedidos desde la mesa o por QR y fideliza a tus comensales con un POS pensado para F&B.',
      children: ['Cafés', 'Servicio rápido', 'Bares', 'Food trucks', 'Catering', 'Panaderías'],
      banner: restaurant,
      link: '/registro',
      faqs: [
        { q: 'Acepta todos los tipos de pago', a: 'Cobra en línea, sin conexión, desde la mesa o con código QR, con tarifas transparentes.' },
        { q: 'Administra tus pedidos en un solo lugar', a: 'Organiza pedidos internos, en línea y por QR sin caos de tabletas.' },
        { q: 'Fideliza a tus comensales', a: 'Marketing y fidelización integrados para que tus clientes regresen.' },
      ],
      testimonial: { quote: 'TecoPOS transformó la operación de Meraki. Cerramos caja en minutos.', name: 'Laura Méndez', role: 'Cafetería · La Habana' },
    },
    {
      id: 'stores',
      iconKey: 'shopping',
      label: 'Tiendas',
      tagline: 'Para retail físico y online',
      description:
        'Inventario sincronizado entre punto de venta y tienda online, con etiquetado, lotes y promociones avanzadas.',
      children: ['Ropa', 'Hogar y regalos', 'Vino y licor', 'Comestibles', 'Jardín'],
      banner: retail,
      link: '/registro',
      faqs: [
        { q: 'Inventario omnicanal en tiempo real', a: 'Stock unificado entre tu tienda física y online, siempre al día.' },
        { q: 'Promociones avanzadas', a: 'Descuentos, combos y campañas configurables por temporada.' },
      ],
      testimonial: { quote: 'Sé qué se vende y qué no en cada tienda, al instante.', name: 'Marta Díaz', role: 'Retail · La Habana' },
    },
    {
      id: 'beauty',
      iconKey: 'sparkles',
      label: 'Belleza',
      tagline: 'Para salones, barberías y spas',
      description:
        'Agenda en línea, comisiones por profesional y fidelización para que tus clientes siempre vuelvan.',
      children: ['Barberías', 'Peluquerías', 'Spas', 'Uñas', 'Estética'],
      banner: barber,
      link: '/registro',
      faqs: [
        { q: 'Agenda y reservas en línea', a: 'Tus clientes reservan solos; tú controlas la disponibilidad por profesional.' },
        { q: 'Comisiones por profesional', a: 'Calcula automáticamente las comisiones de cada miembro del equipo.' },
      ],
      testimonial: { quote: 'La agenda y las comisiones se gestionan solas. Ganamos tiempo.', name: 'Carlos Ruiz', role: 'Barbería · La Habana' },
    },
    {
      id: 'services',
      iconKey: 'wrench',
      label: 'Servicios',
      tagline: 'Para servicios técnicos y profesionales',
      description:
        'Presupuestos, órdenes de trabajo, cobro en sitio y control de equipo para servicios a domicilio o en taller.',
      children: ['Talleres', 'Mantenimiento', 'Instalaciones', 'Consultoría'],
      banner: hardware,
      link: '/registro',
      faqs: [
        { q: 'Presupuestos y órdenes de trabajo', a: 'Crea cotizaciones, conviértelas en órdenes y factura al terminar.' },
        { q: 'Cobra en el sitio', a: 'POS móvil con tarjeta, transferencia o efectivo y recibo digital al instante.' },
      ],
      testimonial: { quote: 'Cobramos en el sitio y enviamos factura al instante. 10x más profesional.', name: 'Iván Pérez', role: 'Servicios técnicos' },
    },
  ],
  ecosystem: [
    { id: 'e1', iconKey: 'cart', label: 'POS', description: 'Cobros rápidos, multi-caja y modo offline. El corazón de tu operación de ventas.', buttonLabel: '', buttonHref: '' },
    { id: 'e2', iconKey: 'package', label: 'Inventario', description: 'Stock en tiempo real con lotes, series y multi-almacén.', buttonLabel: '', buttonHref: '' },
    { id: 'e3', iconKey: 'chart', label: 'Reportes', description: 'KPIs y márgenes en vivo para decidir con datos.', buttonLabel: '', buttonHref: '' },
    { id: 'e4', iconKey: 'card', label: 'Tarjetas', description: 'Monederos digitales con saldo y fidelización.', buttonLabel: '', buttonHref: '' },
    { id: 'e5', iconKey: 'sparkles', label: 'Fidelización', description: 'Promociones, puntos y campañas para que tus clientes regresen.', buttonLabel: '', buttonHref: '' },
    { id: 'e6', iconKey: 'wallet', label: 'Crédito', description: 'Consumos internos controlados para empleados.', buttonLabel: '', buttonHref: '' },
    { id: 'e7', iconKey: 'clock', label: 'Caja', description: 'Apertura, cierre y arqueo digital de turnos.', buttonLabel: '', buttonHref: '' },
    { id: 'e8', iconKey: 'building', label: 'Multi-sucursal', description: 'Un solo panel para todo el grupo.', buttonLabel: '', buttonHref: '' },
    { id: 'e9', iconKey: 'globe', label: 'Tienda online', description: 'Catálogo sincronizado en tiempo real con el inventario.', buttonLabel: '', buttonHref: '' },
  ],
  carousel: [],
  pricing: [
    { id: 'pl1', name: 'Básico', tag: 'Ideal para empezar', price: 'Desde', priceMonthly: '$25', priceAnnual: '$20', annualBadge: 'Ahorra 20%', annualOnly: false, features: ['Punto de venta', 'Inventario básico', '1 caja', 'Soporte por chat'], popular: false, ctaLabel: 'Solicitar cotización', ctaHref: '/registro' },
    { id: 'pl2', name: 'Pro', tag: 'Más popular', price: 'Desde', priceMonthly: '$49', priceAnnual: '$39', annualBadge: 'Ahorra 20%', annualOnly: false, features: ['Todo Básico', 'Tienda online', 'Reportes avanzados', 'Tarjetas de fidelización', 'Hasta 5 cajas'], popular: true, ctaLabel: 'Solicitar cotización', ctaHref: '/registro' },
    { id: 'pl3', name: 'Multi-sucursal', tag: 'Para grupos', price: 'Desde', priceMonthly: '$89', priceAnnual: '$71', annualBadge: 'Ahorra 20%', annualOnly: false, features: ['Todo Pro', 'Multi-sucursal', 'Consolidación de datos', 'Permisos por rol', 'Soporte prioritario'], popular: false, ctaLabel: 'Solicitar cotización', ctaHref: '/registro' },
    { id: 'pl4', name: 'Empresarial', tag: 'A medida', price: 'Cotización personalizada', priceMonthly: '', priceAnnual: '', annualBadge: '', annualOnly: false, features: ['Integraciones a medida', 'SLA dedicado', 'Onboarding asistido', 'Gerente de cuenta'], popular: false, ctaLabel: 'Contactar ventas', ctaHref: '/registro' },
  ],
  // Semilla de `/descargas`. Cada enlace se COMPROBÓ abriéndolo el 2026-09-22;
  // lo que no respondió queda vacío a propósito (una insignia que lleva a un
  // 404 es peor que ninguna insignia) y aparecerá cuando alguien pegue la URL
  // en el editor. Las versiones son las del FICHERO que se enlaza, no las de
  // `app.json`: es lo que se va a descargar de verdad.
  downloads: {
    title: 'Descarga TecoPOS donde trabajas.',
    // La tarjeta `d-pv-windows` toma versión y enlace del `latest.yml` de esta
    // carpeta (vía `GET /downloads/pv-desktop/latest`); su `windowsUrl` queda
    // de respaldo para cuando el servidor no puede leerlo.
    pvDesktopChannelUrl: 'https://repo.tecopos.com/PV_Desktop/',
    intro:
      'Las aplicaciones del ecosistema para el teléfono, la tablet, la televisión y la caja de Windows. Donde la app está en una tienda, la insignia lleva a la tienda; donde no, el instalador se descarga desde aquí.',
    apps: [
      {
        id: 'd-pos',
        name: 'TecoPOS · Punto de venta',
        tagline: 'Vende, cobra e imprime desde el teléfono.',
        description:
          'El punto de venta móvil: órdenes, cobros en varias monedas, impresoras Bluetooth y cierre de turno. Es la aplicación con la que operan la mayoría de los negocios.',
        icon: '',
        platforms: ['android'],
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.threefaces.tecopos',
        appStoreUrl: '',
        apkUrl: '',
        apkVersion: '',
        windowsUrl: '',
        windowsVersion: '',
        visible: true,
      },
      {
        id: 'd-pv-windows',
        name: 'TecoPOS · Punto de venta para Windows',
        tagline: 'La caja de escritorio, con impresoras y gaveta.',
        description:
          'Se instala en la PC de la caja, vincula las impresoras, abre la gaveta y se actualiza sola cuando no hay turno abierto.',
        icon: '',
        platforms: ['windows'],
        playStoreUrl: '',
        appStoreUrl: '',
        apkUrl: '',
        apkVersion: '',
        // Respaldo: lo que se enseña si el servidor no puede leer el
        // `latest.yml`. La carpeta buena es `PV_Desktop` (con mayúsculas;
        // `pv_desktop` da 404); comprobado el 2026-10-28.
        windowsUrl: 'https://repo.tecopos.com/PV_Desktop/TecoposPV-Setup-1.1.11.exe',
        windowsVersion: '1.1.11',
        visible: true,
      },
      {
        id: 'd-gestion',
        name: 'Gestión Plus',
        tagline: 'Las métricas del negocio en el bolsillo.',
        description:
          'Ventas, inventario y caja de todos tus negocios desde el teléfono, sin abrir el panel.',
        icon: '',
        platforms: ['android'],
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.threefaces.gestion',
        appStoreUrl: '',
        apkUrl: '',
        apkVersion: '',
        windowsUrl: '',
        windowsVersion: '',
        visible: true,
      },
      {
        id: 'd-sismico',
        name: 'TecoPOS Sísmico',
        tagline: 'El punto de venta que sigue vendiendo sin conexión.',
        description:
          'Pensado para donde la red va y viene: vende sin internet y sincroniza cuando vuelve.',
        icon: '',
        platforms: ['android'],
        playStoreUrl:
          'https://play.google.com/store/apps/details?id=com.threefacesgroup.tecoposseismic',
        appStoreUrl: '',
        apkUrl: 'https://repo.tecopos.com/Tecopos_Sismico_1.2.1.apk',
        apkVersion: '1.2.1',
        windowsUrl: '',
        windowsVersion: '',
        visible: true,
      },
      {
        id: 'd-kiosko',
        name: 'TecoPOS Kiosko',
        tagline: 'Autoservicio en tablet.',
        description:
          'El cliente pide y paga solo desde una tablet en el local; el pedido sale por la impresora de cocina.',
        icon: '',
        platforms: ['android'],
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.tecopos.kiosko',
        appStoreUrl: '',
        apkUrl: 'https://repo.tecopos.com/apks/kiosko_1.3.3.apk',
        apkVersion: '1.3.3',
        windowsUrl: '',
        windowsVersion: '',
        visible: true,
      },
      {
        id: 'd-tecotv',
        name: 'TecoTV',
        tagline: 'Turnos, carta y cartelería en la pantalla del local.',
        description:
          'Se instala en un Android TV o en una caja de TV y se empareja con el negocio con un código.',
        icon: '',
        platforms: ['tv'],
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.anonymous.turnero',
        appStoreUrl: '',
        apkUrl: 'https://repo.tecopos.com/apks/TecoTV_1.4.0.apk',
        apkVersion: '1.4.0',
        windowsUrl: '',
        windowsVersion: '',
        visible: true,
      },
      {
        id: 'd-sinterceros',
        name: 'Sin Terceros',
        tagline: 'Compra directo a los negocios de tu ciudad.',
        description:
          'El marketplace del ecosistema: los productos de los negocios TecoPOS, para pedirlos desde el teléfono.',
        icon: '',
        platforms: ['android'],
        playStoreUrl: '',
        appStoreUrl: '',
        apkUrl: '',
        apkVersion: '',
        windowsUrl: '',
        windowsVersion: '',
        visible: true,
      },
      {
        id: 'd-mensajero',
        name: 'TecoPOS Mensajero',
        tagline: 'Las entregas del día, en el teléfono del repartidor.',
        description:
          'Ve las entregas que le tocan, navega la ruta y marca cada parada. Cuando la app se publique, la tarjeta enseñará sus enlaces sin tocar la web.',
        icon: '',
        platforms: ['android'],
        playStoreUrl: '',
        appStoreUrl: '',
        apkUrl: '',
        apkVersion: '',
        windowsUrl: '',
        windowsVersion: '',
        visible: true,
      },
    ],
  },
  contact: {
    whatsapp: '5350000000',
    whatsappMessage: 'Hola, me interesa TecoPOS para mi negocio.',
    tecobotUrl: '',
  },
  social: {
    whatsappChannel: '',
    youtube: '',
    facebook: '',
    instagram: '',
  },
  terms: `# Términos y condiciones

_Última actualización: enero de 2026_

Bienvenido a TecoPOS. Al usar nuestra plataforma aceptas los siguientes términos.

## 1. Uso del servicio

TecoPOS te ofrece herramientas para gestionar tu negocio (punto de venta, inventario, tienda online y más). Te comprometes a usar el servicio de forma lícita y a no afectar su funcionamiento.

## 2. Cuentas

Eres responsable de mantener la confidencialidad de tus credenciales y de toda la actividad que ocurra bajo tu cuenta.

## 3. Pagos y facturación

Los servicios contratados se facturan según el plan elegido. Las condiciones de pago, cancelación y reembolso se detallan en tu factura y en tu plan.

## 4. Disponibilidad

Trabajamos para mantener el servicio disponible, considerando las condiciones de conectividad. No garantizamos una disponibilidad ininterrumpida.

## 5. Contacto

Para cualquier consulta sobre estos términos, escríbenos por los canales oficiales de TecoPOS.
`,
  // Semilla redactada contra lo que las apps hacen de verdad (permisos,
  // proveedores y plazos leídos del código el 2026-09-28, contrastados frase a
  // frase por una revisión aparte) y contra el checklist de «Datos de usuario»
  // de Google Play. Si una app empieza a recoger algo nuevo, este texto y el
  // formulario de seguridad de los datos de Play cambian a la vez. Decisiones
  // de Adrian (2026-09-28): el responsable es «Three Faces», tal cual figura en
  // Play; el contacto es info@tecopos.com; la responsabilidad se reparte entre
  // Three Faces y cada negocio; no se dice qué pasa con los pedidos de quien da
  // de baja su cuenta (los pedidos SÍ se declaran como dato tratado); el trazado final
  // de cada ruta de RUMBI se conserva sin plazo. Y el borrado: lo que añadió
  // este cambio dice lo que pasa HOY (la cuenta se da de baja: Identidad y la
  // tienda solo hacen borrado blando), y lo que daría problema con Google —que
  // los datos se conservan— se omite; lo que ya decía la política anterior
  // («eliminación de tus datos» en derechos) se deja como estaba.
  // 🚨 No afirmar cifrados que no existen: la base local del punto de venta va
  // SIN cifrar (decisión 9 de su épica) y RUMBI guarda su sesión en MMKV sin
  // cifrar; por eso el texto no promete ninguno de los dos.
  privacy: `# Política de privacidad

_Última actualización: 28 de septiembre de 2026_

Esta política explica qué datos personales tratan TecoPOS y sus aplicaciones, para qué los usamos, con quién los compartimos, cuánto tiempo los guardamos y cómo puedes dar de baja tu cuenta.

## 1. Quiénes somos

TecoPOS es una plataforma de **Three Faces**, que desarrolla y publica sus aplicaciones, también en Google Play. En esta política, «nosotros» es Three Faces.

Esta política se aplica al sitio tecopos.com, al portal y a estas aplicaciones:

- **TecoPOS · Punto de venta** (TECOPOS SEVEN) y **TecoPOS Sísmico**, para vender en el negocio.
- **Gestión Plus**, para administrar el negocio desde el móvil.
- **TecoPOS Kiosko**, para que los clientes hagan sus pedidos en el local.
- **TecoTV**, la pantalla que muestra los turnos en el local.
- **RUMBI**, la app de los mensajeros que reparten los pedidos.
- Las **tiendas en línea** que publicamos para los negocios con su propia marca (por ejemplo, RG, Galia o Rosto).
- **Sin Terceros**, el mercado en línea.

Para cualquier consulta sobre tus datos, escríbenos a [info@tecopos.com](mailto:info@tecopos.com).

## 2. Quién decide sobre tus datos

TecoPOS lo usan muchos negocios distintos, así que la responsabilidad se reparte:

- **Three Faces** responde de las cuentas de la plataforma, del funcionamiento de las aplicaciones y de los datos que necesitamos para prestar el servicio y cobrarlo.
- **Cada negocio** decide qué datos de sus clientes y de sus empleados registra en TecoPOS: sus ventas, sus pedidos, sus fichas de clientes, las direcciones de entrega o los datos de su personal. Nosotros tratamos esos datos por encargo del negocio y siguiendo sus instrucciones. Si eres cliente o empleado de un negocio, también puedes dirigirte directamente a él.

## 3. Qué datos recogemos

**Datos de tu cuenta.** Nombre, correo electrónico o teléfono, y contraseña. Si decides darlos, también sexo, fecha de nacimiento, carné de identidad y foto de perfil. La contraseña nunca se guarda legible: solo guardamos una huella cifrada que permite comprobarla.

**Datos del negocio y de su actividad.** Productos, precios, inventario, ventas, pedidos, pagos, cierres de caja y facturas, y qué empleado hizo cada operación.

**Datos de los clientes de un negocio.** Si compras en una tienda en línea o haces un pedido con entrega, por la aplicación o por WhatsApp: tu nombre, teléfono, correo, dirección de entrega y los pedidos que haces, y los datos de tu ficha que el negocio decida pedirte. Si eres quien recibe un pedido, el mensajero ve tu nombre, tu dirección y tu teléfono para poder entregártelo, aunque tú no uses ninguna de nuestras aplicaciones.

**Datos de los empleados de un negocio.** Si el negocio gestiona su personal con TecoPOS, registra de cada persona: nombre, carné de identidad, sexo, fecha de nacimiento, estado civil, grupo sanguíneo, foto, salarios y las horas de entrada y salida.

**Ubicación.**

- **RUMBI** registra el recorrido del mensajero **solo mientras tiene una ruta de reparto en curso**: desde que empieza el reparto (lo inicia él con «Empezar reparto» o el negocio desde su panel) hasta que la ruta termina. Lo hace también con la aplicación cerrada o la pantalla apagada (ubicación en segundo plano), y mientras graba se ve un aviso fijo en el teléfono. La aplicación explica en pantalla para qué la necesita. Mientras reparte, RUMBI también envía la posición del momento para que el negocio sepa por dónde va cada pedido. Además usa la ubicación, con la aplicación abierta, para enseñar al mensajero dónde está en el mapa. Fuera del reparto, RUMBI no registra ni envía tu ubicación.
- El **punto de venta** y **TecoPOS Sísmico** piden el permiso de ubicación porque Android lo exige para encontrar impresoras, lectores y otros equipos por Bluetooth. No leen ni guardan tu posición.

**Cámara y fotos.** Algunas aplicaciones usan la cámara o tu galería solo cuando tú lo pides, por ejemplo para la foto de un producto o de tu perfil. Solo guardamos las fotos que decides subir.

**Micrófono.** El punto de venta declara el permiso de micrófono, pero no graba audio.

**Bluetooth, NFC y USB.** Los usamos para conectar impresoras, cajas de dinero y lectores. El punto de venta también lee tarjetas o etiquetas NFC para registrar la entrada y la salida del personal.

**Identificadores del dispositivo.** El identificador y el modelo del teléfono o la tableta, para reconocer los equipos autorizados de cada negocio y mantener la sesión abierta.

**Notificaciones.** Para enviarte avisos, por ejemplo de un pedido nuevo, guardamos un identificador de notificaciones de tu teléfono, que genera Google (Firebase Cloud Messaging).

**Dirección IP.** Nuestros servidores ven la dirección IP desde la que te conectas, y la usamos para proteger el servicio de abusos.

**Informes de errores.** Si una aplicación falla, se envía un informe con el modelo del dispositivo, la versión del sistema y de la aplicación, y lo que estaba pasando. Según la aplicación, el informe puede incluir quién la estaba usando (su identificador, su nombre o su correo) para poder ayudarle. Estos informes no incluyen contraseñas.

**Datos de uso.** Varias aplicaciones registran de forma estadística qué pantallas y funciones se usan, para mejorar el producto.

**En tu teléfono.** Las aplicaciones guardan en el propio dispositivo lo que necesitan para funcionar sin conexión.

## 4. Para qué los usamos

- Prestar el servicio: vender, cobrar, gestionar pedidos, inventario, personal y entregas, y seguir funcionando sin conexión.
- Crear y proteger tu cuenta, y mantener tu sesión en tus dispositivos.
- Enviarte avisos sobre tu trabajo o tus pedidos.
- Dar soporte, corregir errores y mejorar las aplicaciones.
- Facturar nuestros servicios a los negocios.
- Cumplir las obligaciones que nos imponga la ley.

No vendemos tus datos ni los usamos para mostrarte publicidad.

## 5. Con quién los compartimos

- **El negocio con el que trabajas o al que compras** ve los datos de su propia actividad: sus ventas, sus pedidos, sus clientes y sus empleados y, en RUMBI, el recorrido de sus mensajeros durante el reparto.
- **Proveedores que nos prestan servicios**, solo para lo que necesitamos de ellos:
  - **Google (Firebase)**: notificaciones, informes de errores y estadísticas de uso.
  - **Sentry**: informes de errores.
  - **Cloudflare**: protege y entrega nuestro sitio web y los servidores con los que hablan las aplicaciones.
  - **WhatsApp (Meta)**: si el negocio usa los avisos por WhatsApp, por ahí se envían el detalle del pedido a quien compra y a quien lo recibe, y códigos de verificación.
  - **Expo**: las actualizaciones de algunas aplicaciones.
  - **Mapbox**: los mapas del portal web.
  - Servicios de alojamiento y de registros técnicos de nuestros servidores.
  - **Pasarelas de pago**: si pagas en línea, el pago se hace en la página de una pasarela externa, que trata tus datos de pago según su propia política.
- **Las autoridades**, cuando una ley nos obligue.

Algunos de estos proveedores tratan los datos fuera de tu país.

## 6. Cuánto tiempo los guardamos

- Los datos de tu cuenta, mientras la cuenta exista.
- Los puntos de ubicación que graba RUMBI se borran a los 7 días de terminar la ruta y, en cualquier caso, a los 30 días. Lo que se conserva es el trazado final de cada ruta (por dónde pasó, cuándo empezó y terminó y la distancia), como parte del historial de repartos del negocio.
- La posición del mensajero en el momento se borra sola una hora después de su última actualización.
- Los datos de la actividad de un negocio, mientras el negocio use TecoPOS.

## 7. Cómo los protegemos

- Las aplicaciones y el sitio web se comunican con nuestros servidores por conexiones cifradas.
- Las contraseñas nunca se guardan legibles.
- Cada persona solo ve lo que permite su papel en el negocio.

## 8. Tus derechos

Puedes pedirnos en cualquier momento ver los datos que tenemos sobre ti, corregirlos o borrarlos, escribiendo a [info@tecopos.com](mailto:info@tecopos.com). Los permisos de ubicación, cámara y notificaciones puedes retirarlos cuando quieras desde los ajustes del teléfono. Si tus datos los registró un negocio, por ejemplo como cliente o empleado suyo, también puedes pedírselo a él.

## 9. Dar de baja tu cuenta

Puedes pedir que demos de baja tu cuenta cuando quieras. Te explicamos cómo en [Dar de baja tu cuenta](/eliminar-cuenta).

## 10. Menores

Nuestras aplicaciones no están dirigidas a menores de 13 años y no recogemos a sabiendas datos de menores.

## 11. Cambios en esta política

Si cambiamos esta política, publicaremos la versión nueva en esta página y actualizaremos la fecha del principio.

## 12. Contacto

Three Faces — [info@tecopos.com](mailto:info@tecopos.com).
`,
  // Lo que Google Play exige para las apps que permiten crear una cuenta: una
  // URL que explique cómo pedirlo. Dice lo que pasa HOY: la cuenta se da de
  // baja (borrado blando en Identidad y en la tienda); que los datos se
  // conservan se omite (Adrian, 2026-09-28). ⚠️ Google no da por buena una desactivación en las
  // apps con registro (tiendas y Sin Terceros); Adrian decidió no tocarlas
  // ahora (2026-09-28) y centrarse en RUMBI.
  accountDeletion: `# Dar de baja tu cuenta

_Última actualización: 28 de septiembre de 2026_

Esta página explica cómo pedir que demos de baja tu cuenta de TecoPOS o de cualquiera de las aplicaciones de **Three Faces**: TecoPOS · Punto de venta, TecoPOS Sísmico, Gestión Plus, TecoPOS Kiosko, TecoTV, RUMBI, las tiendas en línea de los negocios (como RG, Galia o Rosto) y Sin Terceros.

## Cómo pedirlo

**Por correo.** Escribe a [info@tecopos.com](mailto:info@tecopos.com) con el asunto «Eliminar mi cuenta», desde el correo de tu cuenta si entras con correo, e indica:

- tu nombre y el correo o el teléfono con el que entras;
- la aplicación o las aplicaciones que usas;
- si eres empleado o mensajero, el negocio en el que trabajas.

Antes de darla de baja te contactaremos por el correo o el teléfono de tu cuenta para confirmar que la petición es tuya.

**Desde una tienda en línea.** Entra en tu perfil y pulsa «Eliminar cuenta». Así tu cuenta deja de estar en esa tienda. Si quieres dar de baja tu cuenta en todo TecoPOS, escríbenos al correo de arriba.

## Qué pasa con tu cuenta

Tu cuenta deja de funcionar: ya no podrás entrar con ella y deja de aparecer en la plataforma.

## Si eres cliente de un negocio

Los datos que un negocio registró sobre ti como cliente suyo también puedes pedírselos directamente a ese negocio.

## Si eres empleado o mensajero

Tu cuenta la crea y la administra el negocio en el que trabajas. Escríbenos al correo de arriba o pídele al negocio que la dé de baja.

Más detalles en nuestra [Política de privacidad](/policy).
`,
  aiConnector: aiConnectorSeed,
  aiConnectorPrivacy: aiConnectorPrivacySeed,
  hidden: {},
}

type SectionKey = keyof LandingContent

/**
 * Estado de la carga del contenido:
 * - `loading` → aún no hay contenido real que mostrar (las vistas públicas
 *   renderizan esqueletos, NUNCA los defaults).
 * - `ready`   → hay contenido real (del backend o de la caché local de la
 *   última visita).
 * - `error`   → agotados los reintentos sin contenido real disponible; las
 *   vistas muestran un estado de error con opción de reintentar.
 */
export type LandingContentStatus = 'loading' | 'ready' | 'error'

interface LandingContentStore {
  content: LandingContent
  status: LandingContentStatus
  savingSection: SectionKey | null
  dirty: Partial<Record<SectionKey, boolean>>
  error: string | null
  /** `retryDelaysMs` solo se parametriza en tests; en la app usa el backoff por defecto. */
  load: (retryDelaysMs?: number[]) => Promise<void>
  update: <K extends SectionKey>(key: K, value: LandingContent[K]) => void
  saveSection: (key: SectionKey) => Promise<void>
  reset: () => void
}

/**
 * Une el contenido del backend con los defaults para garantizar que existan todas
 * las claves aunque el documento guardado sea de una versión anterior.
 */
function withDefaults(partial: Partial<LandingContent> | null): LandingContent {
  return { ...DEFAULT_LANDING_CONTENT, ...(partial ?? {}) }
}

const ALL_KEYS = Object.keys(DEFAULT_LANDING_CONTENT) as SectionKey[]

/**
 * Caché local del ÚLTIMO contenido real recibido del backend (no de los
 * defaults). Con conectividad lenta, las visitas repetidas ven el contenido
 * real de la última vez al instante mientras se revalida en segundo plano;
 * los esqueletos quedan solo para la primera visita.
 */
const CACHE_KEY = 'tecopos-portal:landing-content:v1'

function readCache(): Partial<LandingContent> | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as Partial<LandingContent> | null
    return data && typeof data === 'object' ? data : null
  } catch {
    return null
  }
}

function writeCache(content: Partial<LandingContent>): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(content))
  } catch {
    // Cuota excedida (el documento puede traer imágenes como data URL) o
    // storage inaccesible: sin caché; la app sigue funcionando igual.
    try {
      localStorage.removeItem(CACHE_KEY)
    } catch {
      /* nada más que hacer */
    }
  }
}

/** Backoff entre reintentos, pensado para conexiones intermitentes. */
const RETRY_DELAYS_MS = [1500, 4000]

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const useLandingContentStore = create<LandingContentStore>((set, get) => ({
  content: DEFAULT_LANDING_CONTENT,
  status: 'loading',
  savingSection: null,
  dirty: {},
  error: null,

  load: async (retryDelaysMs = RETRY_DELAYS_MS) => {
    if (get().status === 'error') set({ status: 'loading', error: null })

    // Contenido real de una visita anterior: se muestra ya y se revalida.
    const cached = readCache()
    if (cached && get().status === 'loading') {
      set({ content: withDefaults(cached), status: 'ready' })
    }

    for (let attempt = 0; ; attempt++) {
      try {
        const remote = await fetchLandingContent()
        // `remote === null` → la BD aún no tiene contenido guardado: los
        // defaults son la semilla del CMS (mismo documento que ve el editor).
        set({ content: withDefaults(remote), status: 'ready', dirty: {}, error: null })
        if (remote) writeCache(remote)
        return
      } catch (e) {
        if (attempt < retryDelaysMs.length) {
          await delay(retryDelaysMs[attempt])
          continue
        }
        console.error('No se pudo cargar el contenido del landing:', e)
        if (get().status === 'ready') {
          // Había caché: se mantiene el último contenido real conocido.
          set({ error: 'No se pudo actualizar el contenido; se muestra la última versión disponible.' })
        } else {
          // Sin contenido real disponible: NUNCA se enseñan los defaults.
          set({ status: 'error', error: 'No se pudo cargar el contenido. Revisa tu conexión e inténtalo de nuevo.' })
        }
        return
      }
    }
  },

  update: (key, value) =>
    set((state) => ({
      content: { ...state.content, [key]: value },
      dirty: { ...state.dirty, [key]: true },
    })),

  saveSection: async (key) => {
    set({ savingSection: key, error: null })
    try {
      await saveLandingSection(key, get().content[key])
      set((state) => ({ savingSection: null, dirty: { ...state.dirty, [key]: false } }))
    } catch (e) {
      console.error(`No se pudo guardar la sección "${key}":`, e)
      set({ savingSection: null, error: 'No se pudo guardar en el backend.' })
      throw e
    }
  },

  // Restaura los valores por defecto en local; cada sección queda marcada como
  // pendiente de guardar.
  reset: () =>
    set({
      content: DEFAULT_LANDING_CONTENT,
      dirty: Object.fromEntries(ALL_KEYS.map((k) => [k, true])) as Partial<
        Record<SectionKey, boolean>
      >,
    }),
}))

export const useLandingContent = () => useLandingContentStore((s) => s.content)

/** Genera un id corto para nuevos elementos de listas. */
export const newId = () => Math.random().toString(36).slice(2, 9)
